"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqData = [{
  id: "item-1",
  question: "Do we need to move out during a whole-home renovation?",
  answer: "For comprehensive renovations involving multi-room structural changes, primary electrical updates, or full kitchen and plumbing cutoffs, we typically recommend relocating for the most intensive phases. Temporary relocation keeps your family away from airborne drywall dust, noise, and utility shutoffs while allowing our crews to work efficiently. For localized or phased projects, we erect airtight plastic barriers, deploy HEPA air scrubbers, and preserve a clean living zone with functioning utilities."
}, {
  id: "item-2",
  question: "How are Brookhaven municipal permits and inspections managed?",
  answer: "We handle the entire permitting pipeline from submission to final certificate of occupancy. Our team coordinates directly with the City of Brookhaven Community Development Department, managing zoning reviews, tree canopy ordinances, structural plan approvals, and mandatory MEP trade inspections so you never have to navigate city hall."
}, {
  id: "item-3",
  question: "Can our project be phased to accommodate our budget or timeline?",
  answer: "Yes. While executing a full remodel in one continuous build offers the highest cost-efficiency and shortest total calendar duration, we routinely design multi-phase roadmaps. We can complete primary living suites or kitchens first, ensuring rough-in mechanicals are prepped for future phases like basement finishes or exterior additions without having to reopen newly finished drywall later."
}, {
  id: "item-4",
  question: "What architectural drawings and engineering plans are required?",
  answer: "Any project removing or relocating load-bearing walls, altering rooflines, or adding square footage requires stamped structural engineering and detailed architectural plans. Through our design-build process, our architectural partners survey your home, generate detailed CAD drafts and 3D renderings, and secure professional engineer stamps before any hammer swings."
}];
export default function Questions() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="frequently-asked-questions" data-nav="light" className="bg-background text-foreground py-24 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Narrative & Consultation invitation */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col justify-start">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="bg-primary/10 border-primary/30 text-foreground uppercase tracking-widest text-xs px-3 py-1 font-semibold rounded-full">
                Frequently asked questions
              </Badge>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18] mb-5">
              Clear guidance for your whole-home renovation
            </h2>

            <div className="h-px bg-primary/40 w-24 mb-6" />

            <p className="text-foreground/80 text-base sm:text-lg leading-relaxed mb-8">
              Remodeling your home is a major financial and lifestyle commitment. From municipal
              permits across Brookhaven and Buckhead to dust containment and architectural engineering,
              we provide transparent communication from day one.
            </p>

            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm mb-8 space-y-4">
              <div className="flex items-start gap-3.5">
                <div className="size-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Direct builder consultation
                  </h3>
                  <p className="text-sm text-foreground/75 mt-1 leading-normal">
                    Have unique structural constraints or historic neighborhood guidelines? We review your blueprints directly.
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-border/60">
                <Button asChild variant="outline" className="w-full justify-between rounded-lg border-border hover:border-primary/50 text-foreground transition-colors group">
                  <Link to="/ContactRequestAnEstimatePage">
                    <span>Ask a project specialist</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 text-primary" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Accordion */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 bg-card rounded-2xl border border-border/80 p-6 sm:p-8 lg:p-10 shadow-sm">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqData.map((faq, i) => <div data-index={i} key={faq.id}>
                  <AccordionItem value={faq.id} className="border-b border-border/70 py-2 first:pt-0 last:border-b-0">
                    <AccordionTrigger className="text-left font-sans text-base sm:text-lg font-semibold text-foreground hover:no-underline py-4 [&>svg]:text-primary [&>svg]:size-5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-foreground/80 text-sm sm:text-base leading-relaxed pb-5 pr-4 sm:pr-8">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}